import React, { useEffect, useState, useCallback } from "react";
import { authFetch } from "../../Utilities/Api";
import './ModerationDashboard.css';

const ModerationDashboard = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("pending");

    // 🔍 Search & Pagination state
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [logUserId, setLogUserId] = useState(null);
    const [logs, setLogs] = useState([]);

    // ✅ Combined Fetch: Handles Tabs, Search, and Pagination
    const fetchReports = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const statusQuery = activeTab === "pending" ? "pending" : "resolved,dismissed";

            // Append search term (q), page, and limit to the request
            const res = await authFetch(`/reports?status=${statusQuery}&q=${encodeURIComponent(searchTerm)}&page=${page}&limit=10`);
            const data = await res.json();

            if (!res.ok) throw new Error(data.message || "Unexpected response");

            // Handle both paginated object and legacy array fallback
            if (data.reports) {
                setReports(data.reports);
                setTotalPages(data.totalPages || 1);
                setCurrentPage(data.currentPage || 1);
            } else if (Array.isArray(data)) {
                setReports(data);
                setTotalPages(1);
            }
        } catch (err) {
            console.error("[ModerationDashboard] Error:", err);
            setError("Failed to load reports.");
        } finally {
            setLoading(false);
        }
    }, [activeTab, searchTerm]);

    // ⏱️ Debounce logic for Search
    useEffect(() => {
        const delaySearch = setTimeout(() => {
            fetchReports(1); // Reset to page 1 on search/tab change
        }, 400);

        return () => clearTimeout(delaySearch);
    }, [fetchReports]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            fetchReports(newPage);
        }
    };

    const fetchLogs = async (userId) => {
        if (logUserId === userId) {
            setLogUserId(null);
            setLogs([]);
            return;
        }
        setLogUserId(userId);
        try {
            const res = await authFetch(`/users/${userId}/moderation-logs`);
            const data = await res.json();
            setLogs(data);
        } catch (err) {
            console.error("❌ Log Error:", err);
        }
    };

    const handleAction = async (id, action, duration = 0) => {
        const reason = prompt(`Enter reason for ${action}:`);
        if (!reason) return;

        try {
            const res = await authFetch(`/reports/${id}/resolve`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action, reason, duration }),
            });

            if (res.ok) {
                // Remove from local list and refresh to keep pagination counts accurate
                setReports(prev => prev.filter(r => r._id !== id));
                if (reports.length <= 1 && currentPage > 1) fetchReports(currentPage - 1);
                else fetchReports(currentPage);
            } else {
                const err = await res.json();
                alert(err.message || "Failed to resolve report.");
            }
        } catch (err) {
            console.error("❌ Action Error:", err);
        }
    };

    const handleReopen = async (id) => {
        if (!window.confirm("Are you sure you want to reopen this report?")) return;
        try {
            const res = await authFetch(`/reports/${id}/reopen`, { method: "PATCH" });
            if (res.ok) {
                setReports(prev => prev.filter(r => r._id !== id));
                fetchReports(currentPage);
            }
        } catch (err) {
            console.error("❌ Reopen Error:", err);
        }
    };

    return (
        <div className="mod-dashboard page-wrapper stretched-layout">
            <div className="admin-header">
                <h2 className="mod-title">Moderation Dashboard</h2>

                <input
                    type="text"
                    placeholder="Search by username or reason..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-search-bar"
                />
            </div>

            <div className="mod-tabs">
                <button
                    className={`mod-tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('pending'); setSearchTerm(""); setCurrentPage(1); }}
                >
                    🚨 Pending Reviews
                </button>
                <button
                    className={`mod-tab-btn ${activeTab === 'resolved' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('resolved'); setSearchTerm(""); setCurrentPage(1); }}
                >
                    ✅ Resolved History
                </button>
            </div>

            {loading ? (
                <div className="spinner-container"><div className="spinner"></div></div>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : reports.length === 0 ? (
                <div className="mod-empty-state">
                    <p>{activeTab === 'pending' ? "No pending reports! Good job. 🎉" : "No resolved reports found."}</p>
                </div>
            ) : (
                <>
                    <div className="mod-reports-list">
                        {reports.map(report => (
                            <div key={report._id} className="report-card stretched">
                                <div className="report-header">
                                    <span>Reported by: <span className="highlight">{report.reporter?.username || "[deleted]"}</span></span>
                                    <span className="report-date">{new Date(report.createdAt).toLocaleString()}</span>
                                </div>

                                <div className="report-content">
                                    <div className="report-target-info">
                                        <span className="report-type-badge">{report.type}</span>
                                        <h3>Target: {report.target?.username || "[unknown]"}</h3>
                                        <button onClick={() => fetchLogs(report.target?._id)} className="btn-text-link">
                                            {logUserId === report.target?._id ? "Hide History" : "View History"}
                                        </button>
                                    </div>
                                    <div className="report-reason-box full-width">
                                        <strong>Reason:</strong> {report.reason}
                                    </div>
                                </div>

                                <div className="report-actions-area">
                                    {activeTab === 'pending' ? (
                                        <div className="action-btns mod-action-btns">
                                            <button onClick={() => handleAction(report._id, "warn")} className="btn-warn">⚠️ Warn</button>
                                            <button onClick={() => handleAction(report._id, "mute", 86400000)} className="btn-mute">🔇 Mute (24h)</button>
                                            <button onClick={() => handleAction(report._id, "ban", 259200000)} className="btn-ban">🔨 Ban (3d)</button>
                                            <button onClick={() => handleAction(report._id, "ban", 0)} className="btn-perm-ban">🚫 Perm Ban</button>
                                            <div className="spacer"></div>
                                            <button onClick={() => handleAction(report._id, "dismiss")} className="btn-cancel">Dismiss</button>
                                        </div>
                                    ) : (
                                        <div className="report-resolved-box">
                                            <div>
                                                <p className="resolved-status"><strong>Resolved:</strong> {report.action?.toUpperCase()}</p>
                                                <p className="resolved-details">By <span>{report.moderator?.username || "Admin"}</span> on {new Date(report.resolvedAt).toLocaleDateString()}</p>
                                            </div>
                                            <button onClick={() => handleReopen(report._id)} className="btn-text-link">Reopen</button>
                                        </div>
                                    )}
                                </div>

                                {logUserId === report.target?._id && (
                                    <div className="report-logs-container stretched-logs">
                                        <h4>History for {report.target?.username}</h4>
                                        {logs.length > 0 ? (
                                            <ul>
                                                {logs.map((log, i) => (
                                                    <li key={i} className="log-item-stretched">
                                                        <span><span className={`log-type ${log.type}`}>{log.type}</span>: {log.reason}</span>
                                                        <span className="log-date">{new Date(log.date).toLocaleDateString()}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : <p className="no-history">No prior history.</p>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* 📄 Pagination controls aligned with Manage Users style */}
                    {totalPages > 1 && (
                        <div className="pagination mod-pagination">
                            <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>« Prev</button>
                            <span className="page-info">Page <strong>{currentPage}</strong> of {totalPages}</span>
                            <button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next »</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ModerationDashboard;